import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { usePicksData } from '../hooks/usePicksData';
import { useCachedParticipants } from '../hooks/useCachedParticipants';
import { FOOTBALL_TEAMS, saveAllPicks, saveUserPicksWithLink, isAdmin, updateWeekResults } from '../services/apiService';
import { excludedTeams } from '../services/teamSelections';
import GameOddsPanel from './GameOddsPanel';
import CreateUserModal from './CreateUserModal';
import DeleteUserConfirmModal from './DeleteUserConfirmModal';

const TableContainer = styled.div`
  padding: 20px;
  max-width: 100%;
  overflow-x: auto;
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};

  h1 {
    text-align: center;
    margin-bottom: 20px;
    font-size: 28px;
    color: ${props => props.theme.colors.text};
  }
`;

const Message = styled.div`
  text-align: center;
  padding: 20px;
  border-radius: 5px;
  margin-bottom: 20px;
  font-weight: 500;

  p {
    margin: 5px 0;
  }
`;

const LoadingMessage = styled(Message)`
  background-color: ${props => props.theme.colors.messageBgLight};
  border: 1px solid ${props => props.theme.colors.messageBorderLight};
  color: ${props => props.theme.colors.messageTextLight};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 140px;
`;

const LoadingSpinner = styled.div`
  position: relative;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  border: 4px solid rgba(52, 152, 219, 0.2);
  border-top-color: #3498db;
  animation: spin 0.9s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoadingFootball = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 28px;
  animation: footballBounce 1s ease-in-out infinite;

  @keyframes footballBounce {
    0%, 100% {
      transform: translate(-50%, -50%) scale(1) rotate(0deg);
    }
    50% {
      transform: translate(-50%, -54%) scale(1.1) rotate(8deg);
    }
  }
`;

const LoadingSubtext = styled.p`
  font-size: 13px;
  opacity: 0.85;
`;

const ErrorMessage = styled(Message)`
  background-color: #6a1b1b;
  border: 1px solid #8b2e2e;
  color: #ff8b8b;
`;

const ErrorSubtext = styled.p`
  font-size: 12px;
  color: #ffb3b3;
  margin-top: 8px !important;
`;

const SuccessMessage = styled(Message)`
  background-color: #1b4332;
  border: 1px solid #2d6a4f;
  color: #95d5b2;
  font-weight: 600;
`;

const WeekNavigation = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  margin-bottom: 20px;
  padding: 15px;
  background-color: ${props => props.theme.colors.navBgColor};
  border-radius: 8px;
  flex-wrap: wrap;
`;

const NavButton = styled.button`
  padding: 10px 16px;
  background-color: ${props => props.theme.colors.buttonBgColor};
  color: ${props => props.theme.colors.buttonTextColor};
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background-color: ${props => props.theme.colors.buttonHoverBg};
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:disabled {
    background-color: #bdc3c7;
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const WeekSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Label = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  white-space: nowrap;
`;

const Dropdown = styled.select`
  padding: 8px 12px;
  border: 2px solid ${props => props.theme.colors.buttonBgColor};
  border-radius: 5px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  background-color: ${props => props.theme.colors.tableBgColor};
  color: ${props => props.theme.colors.text};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${props => props.theme.colors.buttonHoverBg};
    box-shadow: 0 2px 6px rgba(52, 152, 219, 0.2);
  }

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.buttonHoverBg};
    box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.1);
  }
`;

const CreateUserButton = styled.button`
  padding: 10px 16px;
  background-color: #27ae60;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background-color: #229954;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:disabled {
    background-color: #bdc3c7;
    cursor: not-allowed;
    opacity: 0.6;
  }
`;


const Legend = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  flex-wrap: wrap;
  justify-content: center;
  padding: 10px;
  background-color: ${props => props.theme.colors.navBgColor};
  border-radius: 5px;
`;

const LegendItem = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: ${props => props.theme.colors.text};
`;

const LegendBox = styled.span`
  display: inline-block;
  width: 20px;
  height: 20px;
  border-radius: 3px;
  background-color: ${props => props.color};
`;

const PicksLayout = styled.div`
  display: grid;
  grid-template-columns: ${props => props.$singleUser ? 'minmax(0, 1fr) minmax(300px, 380px)' : 'minmax(0, 1fr)'};
  grid-template-areas: ${props => props.$singleUser ? "'picks odds'" : "'picks'"};
  gap: 24px;
  align-items: start;

  @media (max-width: 850px) {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas: ${props => props.$singleUser ? "'odds' 'picks'" : "'picks'"};
  }
`;

const PicksContent = styled.div`
  grid-area: picks;
  min-width: 0;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const PicksTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background-color: ${props => props.theme.colors.tableBgColor};
  font-size: 14px;
  color: ${props => props.theme.colors.text};

  thead {
    background-color: #2c3e50;
    color: white;
    position: sticky;
    top: 0;
    z-index: 10;
  }

  th {
    padding: 12px 8px;
    text-align: center;
    font-weight: 600;
    border: 1px solid ${props => props.theme.colors.borderColor};
    white-space: nowrap;
  }

  tbody tr {
    border-bottom: 1px solid ${props => props.theme.colors.borderColor};
    transition: background-color 0.2s ease;

    &:hover {
      background-color: ${props => props.theme.colors.navBgColor};
    }

    &.total-row {
      background-color: ${props => props.theme.colors.navBgColor};
      font-weight: 600;
      border-top: 2px solid #2c3e50;
    }
  }
`;

const LineHeader = styled.th`
  min-width: 60px;
  font-size: 13px;
`;

const ParticipantHeaderCell = styled.th`
  padding: 0 !important;
  background-color: #2c3e50;
`;

const ParticipantHeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 8px 4px;
  min-height: 100%;
`;

const ParticipantName = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: white;
  white-space: nowrap;
`;

const CopyLinkButton = styled.button`
  background: none;
  border: none;
  color: #3498db;
  cursor: pointer;
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 3px;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background-color: rgba(52, 152, 219, 0.2);
    color: #5dade2;
  }

  &:active {
    transform: scale(0.95);
  }
`;


const DeleteUserButton = styled.button`
  padding: 10px 16px;
  background-color: #d32f2f;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background-color: #b71c1c;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:disabled {
    background-color: #bdc3c7;
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const AdminPanel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  margin-bottom: 20px;
  padding: 15px;
  background-color: ${props => props.theme.colors.navBgColor};
  border-radius: 8px;
  flex-wrap: wrap;
`;

const GenericButton = styled.button`
  padding: 10px 16px;
  background-color: #3498db;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background-color: #2980b9;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:disabled {
    background-color: #bdc3c7;
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const LineCell = styled.td`
  padding: 12px 8px;
  text-align: center;
  font-weight: 600;
  background-color: ${props => props.theme.colors.navBgColor};
  min-width: 60px;
  border-right: 2px solid ${props => props.theme.colors.borderColor};
  color: ${props => props.theme.colors.text};
`;

const PickCell = styled.td`
  padding: 4px;
  text-align: center;
  border: 1px solid ${props => props.theme.colors.borderColor};
  min-width: 100px;
  transition: all 0.2s ease;
  background-color: ${props => {
    if (props.$status === 'won') return '#1b4332';
    if (props.$status === 'lost') return '#6a1b1b';
    if (props.$status === 'dns') return '#fff3cd';
    if (props.$status === 'dis') return '#f5c6cb';
    return props.theme.colors.tableBgColor;
  }};
  position: relative;
  opacity: ${props => props.$locked ? 0.9 : 1};

  &::after {
    content: ${props => props.$locked ? "'🔒'" : "''"};
    position: absolute;
    top: 2px;
    right: 2px;
    font-size: 10px;
  }
`;

const PickCellContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  position: relative;
`;

const PickInput = styled.input`
  padding: 6px 4px;
  border: none;
  border-radius: 3px;
  background-color: ${props => {
    if (props.$status === 'won') return '#1b4332';
    if (props.$status === 'lost') return '#6a1b1b';
    if (props.$status === 'dns') return '#fff3cd';
    if (props.$status === 'dis') return '#f5c6cb';
    return props.theme.colors.tableBgColor;
  }};
  color: ${props => {
    if (props.$status === 'won') return '#95d5b2';
    if (props.$status === 'lost') return '#ff8b8b';
    if (props.$status === 'dns') return '#856404';
    if (props.$status === 'dis') return '#721c24';
    return props.theme.colors.text;
  }};
  cursor: ${props => props.disabled ? 'not-allowed' : 'text'};
  font-size: 12px;
  font-weight: ${props => ['won', 'lost', 'dns', 'dis'].includes(props.$status) ? 600 : 500};
  transition: all 0.2s ease;
  width: 100%;
  box-sizing: border-box;
  opacity: ${props => props.disabled ? 0.85 : 1};

  &:hover:not(:disabled) {
    background-color: ${props => props.theme.colors.navBgColor};
  }

  &:focus {
    outline: 2px solid ${props => props.theme.colors.buttonBgColor};
    outline-offset: -1px;
  }
`;

const AutocompleteList = styled.ul`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: ${props => props.theme.colors.tableBgColor};
  border: 1px solid ${props => props.theme.colors.buttonBgColor};
  border-top: none;
  border-radius: 0 0 3px 3px;
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 150px;
  overflow-y: auto;
  z-index: 100;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const AutocompleteItem = styled.li`
  padding: 8px 6px;
  cursor: pointer;
  font-size: 12px;
  color: ${props => props.theme.colors.text};
  transition: background-color 0.15s ease;
  border-bottom: 1px solid ${props => props.theme.colors.borderColor};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${props => props.theme.colors.navBgColor};
    color: ${props => props.theme.colors.buttonBgColor};
    font-weight: 500;
  }
`;

const TotalValue = styled.span`
  display: inline-block;
  padding: 8px 12px;
  background-color: #3498db;
  color: white;
  border-radius: 4px;
  font-weight: 700;
  min-width: 40px;
`;

const Toast = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  background-color: #27ae60;
  color: white;
  padding: 16px 24px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  font-weight: 500;
  font-size: 14px;
  z-index: 1000;
  animation: slideIn 0.3s ease-out;

  @keyframes slideIn {
    from {
      transform: translateX(400px);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const TableComponent = ({ singleUserUrlLink }) => {
  let weekFromLocalStorage = localStorage.getItem('current_week') !== null ? Number(localStorage.getItem('current_week')) : 1;
  const [currentWeek, setCurrentWeek] = useState(weekFromLocalStorage);
  const [currentYear, setCurrentYear] = useState(2026);
  
  // Use cached participants hook (only fetches once)
  const { 
    participants: allParticipants, 
    participantUserIds, 
    participantUrlLinks, 
    refetchParticipants 
  } = useCachedParticipants();
  
  // Use picks data hook with cached participants
  const { 
    picks: weeklyPicks, 
    setPicks, 
    participantFlags, 
    loading, 
    error, 
    refetchData,
    gamesInWeek
  } = usePicksData(currentWeek, currentYear, allParticipants, participantUserIds, participantUrlLinks);
  
  // Filter to single user if singleUserUrlLink is provided
  const participants = singleUserUrlLink 
    ? allParticipants.filter(p => participantUrlLinks[p] === singleUserUrlLink)
    : allParticipants;
  const highestTotal = Math.max(0, ...allParticipants.map(
    participant => Number(weeklyPicks[participant]?.Total?.total) || 0
  ));
  const [matchups, setMatchups] = useState({});
  const currentGames = matchups.key === `${currentYear}-${currentWeek}` ? matchups.games : [];
  const [autocompleteOpen, setAutocompleteOpen] = useState({});
  const [autocompleteValue, setAutocompleteValue] = useState({});
  const [isSavingAllPicks, setIsSavingAllPicks] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(null);
  const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState(false);
  const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen] = useState(false);
  const [isUpdatingResults, setIsUpdatingResults] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);
  const [adminUnlockEnabled, setAdminUnlockEnabled] = useState(false);
  const canEditLockedPicks = adminUnlockEnabled && isAdmin() && !singleUserUrlLink;

  useEffect(() => {
    setAdminUnlockEnabled(false);
    setAutocompleteOpen({});
  }, [currentWeek, currentYear, singleUserUrlLink]);
  const inputRefsMap = useRef({});
  const TOTAL_WEEKS = 18;
  const TOTAL_YEARS = [2026];

  useEffect(() => {
    if (weeklyPicks && Object.keys(weeklyPicks).length > 0) {
      const newAutocompleteValue = {};
      Object.entries(weeklyPicks).forEach(([participant, picks]) => {
        Object.entries(picks).forEach(([line, data]) => {
          if (data.pick) {
            const key = `${participant}-${line}`;
            newAutocompleteValue[key] = data.pick;
          }
        });
      });
      setAutocompleteValue(newAutocompleteValue);
    }
  }, [weeklyPicks]);

  const getTeamSuggestions = (input, allPickedTeams) => {
    const lowerInput = input.toLowerCase();
    const excluded = excludedTeams(allPickedTeams, currentGames, FOOTBALL_TEAMS);
    const suggestions = FOOTBALL_TEAMS.filter(
      (team) =>
        (lowerInput === '' || team.toLowerCase().includes(lowerInput)) &&
        !excluded.has(team)
    );
    return suggestions;
  };

  const getParticipantPickedTeams = (participant) => {
    const picked = [];
    for (let line = 1; line <= 16; line++) {
      const value = autocompleteValue[`${participant}-${line}`];
      if (FOOTBALL_TEAMS.includes(value)) picked.push(value);
    }
    return picked;
  };

  // eslint-disable-next-line no-unused-vars
  const getAllPickedTeams = () => {
    const picked = [];
    Object.values(weeklyPicks).forEach((participantPicks) => {
      Object.values(participantPicks).forEach((lineData) => {
        if (lineData.pick) {
          picked.push(lineData.pick);
        }
      });
    });
    return picked;
  };

  const isAnyLineLockedForParticipant = (participant) => {
    const participantPicks = weeklyPicks[participant] || {};
    for (let i = 1; i <= 16; i++) {
      if (participantPicks[i]?.locked) {
        return true;
      }
    }
    return false;
  };

  const handlePickChange = (participant, line, value) => {
    if (isCellLocked(participant, line, weeklyPicks[participant]?.[line])) return;

    const key = `${participant}-${line}`;
    setAutocompleteValue((prev) => ({ ...prev, [key]: value }));
    
    setPicks((prevWeeklyPicks) => ({
      ...prevWeeklyPicks,
      [participant]: {
        ...prevWeeklyPicks[participant],
        [line]: { ...prevWeeklyPicks[participant][line], pick: value },
      },
    }));
  };

  const toggleAutocomplete = (participant, line) => {
    if (isCellLocked(participant, line, weeklyPicks[participant]?.[line])) return;

    const key = `${participant}-${line}`;
    setAutocompleteOpen((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const selectTeam = (participant, line, team) => {
    handlePickChange(participant, line, team);
    const key = `${participant}-${line}`;
    setAutocompleteOpen((prev) => ({ ...prev, [key]: false }));
    setAutocompleteValue((prev) => ({ ...prev, [key]: team }));
  };

  const handlePreviousWeek = () => {
    if (currentWeek > 1) {
      setCurrentWeek(currentWeek - 1);
    }
  };

  const handleNextWeek = () => {
    if (currentWeek < TOTAL_WEEKS) {
      setCurrentWeek(currentWeek + 1);
    }
  };

  const handleWeekSelect = (week) => {
    setCurrentWeek(week);
  };

  const handleYearSelect = (year) => {
    setCurrentYear(year);
  };
  const handleSaveAllPicks = async () => {
    setIsSavingAllPicks(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {      
      await saveAllPicks(participants, autocompleteValue, weeklyPicks, participantUserIds, currentWeek, currentYear)
      
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      refetchData();
    } catch (err) {
      console.error('Failed to save picks:', err);
      setSaveError(err.message || 'Failed to save picks. Please try again.');
      setTimeout(() => setSaveError(null), 5000);
    } finally {
      setIsSavingAllPicks(false);
    }
  };

  const handleSaveUserPicks = async () => {
    setIsSavingAllPicks(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      if (!participants.length) {
        throw new Error('User not found');
      }

      const participant = participants[0];
      const userId = participantUserIds[participant];
      const username = participant;
      const userLink = singleUserUrlLink;

      if (!userId || !userLink) {
        throw new Error('Missing user information');
      }

      // Extract picks for this single user from autocompleteValue
      const userAutocompleteValue = {};
      Object.entries(autocompleteValue).forEach(([key, value]) => {
        const [user] = key.split('-');
        if (user === participant) {
          userAutocompleteValue[key] = value;
        }
      });

      await saveUserPicksWithLink(userId, username, userAutocompleteValue, weeklyPicks[participant], currentWeek, currentYear);
      
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      refetchData();
    } catch (err) {
      console.error('Failed to save user picks:', err);
      setSaveError(err.message || 'Failed to save picks. Please try again.');
      setTimeout(() => setSaveError(null), 5000);
    } finally {
      setIsSavingAllPicks(false);
    }
  };

  const handleUserCreated = () => {
    refetchParticipants(); // The updated participants trigger a picks refresh
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
  };

  const handleUserDeleted = () => {
    refetchParticipants(); // The updated participants trigger a picks refresh
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
  };

  const handleUpdateResults = async () => {
    setIsUpdatingResults(true);
    setSaveError(null);
    try {
      await updateWeekResults(currentWeek, currentYear);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      refetchData();
    } catch (error) {
      setSaveError(error.message || 'Failed to update week results');
      setTimeout(() => setSaveError(null), 5000);
    } finally {
      setIsUpdatingResults(false);
    }
  };

  const getCellStatus = (participant, line, lineData) => {
    if (!lineData) return null;
    
    const userFlags = participantFlags[participant];
    
    if (userFlags?.dns) return 'dns';
    if (userFlags?.dis) return 'dis';
    
    if (lineData.locked && lineData.winner === true) {
      return 'won';
    }
  
    if (lineData.locked && lineData.winner === false) {
      return 'lost';
    }

    return null;
  };

  const isCellLocked = (participant, line, lineData) => {
    // The admin override affects editability only; all saved flags stay intact.
    if (canEditLockedPicks) return false;
    const userFlags = participantFlags[participant];
    if (userFlags?.dns || userFlags?.dis) return true;
    if (line === 'Tie' && isAnyLineLockedForParticipant(participant)) return true;
    if (lineData?.locked) return true;
    return false;
  };

  const lineNumbers = Array.from({ length: gamesInWeek }, (_, i) => gamesInWeek - i).concat([
    'Tie',
    'Total',
  ]);

  const handleCopyLink = (participant) => {
    const urlLink = participantUrlLinks[participant];
    if (!urlLink) return;
    
    const fullUrl = process.env.REACT_APP_URL ? `${process.env.REACT_APP_URL}${urlLink}` : `https://fantasyfootballtoolz.com/weekly-picks/${urlLink}`;
    navigator.clipboard.writeText(fullUrl).then(() => {
      setToastMessage('Link copied to clipboard!');
      setTimeout(() => setToastMessage(null), 3000);
    }).catch((err) => {
      console.error('Failed to copy link:', err);
      setToastMessage('Failed to copy link');
      setTimeout(() => setToastMessage(null), 3000);
    });
  };

  const handlePickInputKeyDown = (e, participant, line, lineIndex) => {
    // Handle Tab and Shift+Tab for vertical navigation
    if (e.key === 'Tab') {
      e.preventDefault();
      
      const isShiftTab = e.shiftKey;
      
      if (isShiftTab) {
        // Shift+Tab: Move up
        if (lineIndex > 0) {
          const nextLine = lineNumbers[lineIndex - 1];
          if (nextLine !== 'Total') {
            const nextKey = `${participant}-${nextLine}`;
            inputRefsMap.current[nextKey]?.focus();
          }
        }
      } else {
        // Tab: Move down
        if (lineIndex < lineNumbers.length - 2) { // Exclude 'Total' row
          const nextLine = lineNumbers[lineIndex + 1];
          if (nextLine !== 'Total') {
            const nextKey = `${participant}-${nextLine}`;
            inputRefsMap.current[nextKey]?.focus();
          } else if (lineIndex + 2 < lineNumbers.length - 1) {
            // Skip Total row and continue to next
            const nextLine2 = lineNumbers[lineIndex + 2];
            const nextKey = `${participant}-${nextLine2}`;
            inputRefsMap.current[nextKey]?.focus();
          }
        }
      }
    }
    
    // Handle Enter to select first suggestion
    if (e.key === 'Enter' && autocompleteOpen[`${participant}-${line}`]) {
      e.preventDefault();
      
      const currentValue = autocompleteValue[`${participant}-${line}`] || '';
      const suggestions = getTeamSuggestions(currentValue, getParticipantPickedTeams(participant));
      
      if (suggestions.length > 0) {
        selectTeam(participant, line, suggestions[0]);
      }
    }
  };

  return (
    <TableContainer>
      {/* Week Navigation */}
      <WeekNavigation>
        <NavButton 
          onClick={handlePreviousWeek}
          disabled={currentWeek === 1}
        >
          ← Previous
        </NavButton>
        
        <WeekSelector>
          <Label>Week {currentWeek} of {TOTAL_WEEKS}</Label>
          <Dropdown 
            value={currentWeek} 
            onChange={(e) => handleWeekSelect(Number(e.target.value))}
          >
            {Array.from({ length: TOTAL_WEEKS }, (_, i) => i + 1).map((week) => (
              <option key={week} value={week}>
                Week {week}
              </option>
            ))}
          </Dropdown>
        </WeekSelector>
        
        <NavButton 
          onClick={handleNextWeek}
          disabled={currentWeek === TOTAL_WEEKS}
        >
          Next →
        </NavButton>

        <WeekSelector>
          <Label> Football Year </Label>
          <Dropdown 
            value={currentYear} 
            onChange={(e) => handleYearSelect(Number(e.target.value))}
          >
            {TOTAL_YEARS.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </Dropdown>
        </WeekSelector>
      </WeekNavigation>

      <PicksLayout $singleUser={Boolean(singleUserUrlLink)}>
      {singleUserUrlLink && <GameOddsPanel week={currentWeek} year={currentYear} selectedTeams={getParticipantPickedTeams(participants[0])} onGamesLoaded={setMatchups} />}
      <PicksContent>

      {isAdmin() && !singleUserUrlLink && (
        <AdminPanel>
          <GenericButton
            onClick={() => {
              setAdminUnlockEnabled(enabled => !enabled);
              setAutocompleteOpen({});
            }}
            aria-pressed={canEditLockedPicks}
            title="Temporarily allow admin edits for this week; saved lock flags remain unchanged"
          >
            {canEditLockedPicks ? 'Restore Pick Locks' : 'Unlock Picks for the Week'}
          </GenericButton>
          {canEditLockedPicks && (
            <span role="status">Admin editing enabled for Week {currentWeek}, {currentYear}. Save All Picks to save edits.</span>
          )}
          <CreateUserButton 
            onClick={() => setIsCreateUserModalOpen(true)}
            title="Create a new user (admin only)"
          >
            + Create User
          </CreateUserButton>
          <DeleteUserButton 
            onClick={() => setIsDeleteConfirmModalOpen(true)}
            title="Delete an existing user (admin only)"
            disabled={false}
          >
            - Delete User
          </DeleteUserButton>
          <GenericButton 
            onClick={handleUpdateResults}
            title="Update results for the current week (admin only)"
            disabled={isUpdatingResults}
          >
            {isUpdatingResults ? '⟳ Updating...' : '⟳ Update Results'}
          </GenericButton>
          <GenericButton 
            onClick={handleSaveAllPicks}
            title="Save all picks for all participants (admin only)"
            disabled={isSavingAllPicks}
          >
            {isSavingAllPicks ? '💾 Saving...' : '💾 Save All Picks'}
          </GenericButton>
        </AdminPanel>
      )}

      {singleUserUrlLink && (
        <AdminPanel>
          <GenericButton 
            onClick={handleSaveUserPicks}
            title="Save your picks"
            disabled={isSavingAllPicks}
          >
            {isSavingAllPicks ? '💾 Saving...' : '💾 Save Picks'}
          </GenericButton>
        </AdminPanel>
      )}

      {/* Save Status Messages */}
      {saveSuccess && (
        <SuccessMessage>
          <p>✓ Picks saved successfully!</p>
        </SuccessMessage>
      )}

      {saveError && (
        <ErrorMessage>
          <p>⚠️ {saveError}</p>
        </ErrorMessage>
      )}

      {/* Loading and Error States */}
      {loading && (
        <LoadingMessage>
          <LoadingSpinner>
            <LoadingFootball>🏈</LoadingFootball>
          </LoadingSpinner>
          <p>Loading picks data…</p>
          <LoadingSubtext>This can take up to 15 seconds on first load.</LoadingSubtext>
        </LoadingMessage>
      )}

      {error && (
        <ErrorMessage>
          <p>⚠️ {error}</p>
          <ErrorSubtext>Using fallback data or API may be unavailable</ErrorSubtext>
        </ErrorMessage>
      )}

      <Legend>
        <LegendItem>
          <LegendBox color="#1b4332" /> Won
        </LegendItem>
        <LegendItem>
          <LegendBox color="#6a1b1b" /> Lost
        </LegendItem>
        <LegendItem>
          <LegendBox color="#fff3cd" /> DNS (Did Not Send)
        </LegendItem>
        <LegendItem>
          <LegendBox color="#f5c6cb" /> DIS (Disqualified)
        </LegendItem>
      </Legend>

      <TableWrapper>
        <PicksTable>
          <thead>
            <tr>
              <LineHeader>Line</LineHeader>
              {participants.map((participant) => (
                <ParticipantHeaderCell key={participant}>
                  <ParticipantHeaderContent>
                    <ParticipantName>
                      {participant}
                      {highestTotal > 0 && Number(weeklyPicks[participant]?.Total?.total) === highestTotal && (
                        <> <span role="img" aria-label="Points leader" title="Most total points">👑</span></>
                      )}
                    </ParticipantName>
                    {isAdmin() && participantUrlLinks[participant] && (
                      <CopyLinkButton 
                        onClick={() => handleCopyLink(participant)}
                        title={`Copy join link for ${participant}`}
                      >
                        📋 Copy Link
                      </CopyLinkButton>
                    )}
                  </ParticipantHeaderContent>
                </ParticipantHeaderCell>
              ))}
            </tr>
          </thead>
          <tbody>
            {lineNumbers.map((line, lineIndex) => (
              <tr key={line} className={line === 'Total' ? 'total-row' : ''}>
                <LineCell>{line}</LineCell>
                {participants.map((participant) => {
                  const lineData = weeklyPicks[participant]?.[line];
                  const cellStatus = getCellStatus(participant, line, lineData);
                  const isLocked = isCellLocked(participant, line, lineData);

                  return (
                    <PickCell
                      key={`${participant}-${line}`}
                      $status={cellStatus}
                      $locked={isLocked}
                    >
                      {line === 'Total' ? (
                        <TotalValue>
                          {weeklyPicks[participant]?.['Total']?.total ?? '-'}
                        </TotalValue>
                      ) : (
                        <PickCellContent>
                          <PickInput
                            ref={(el) => {
                              if (el) {
                                inputRefsMap.current[`${participant}-${line}`] = el;
                              }
                            }}
                            type={line === 'Tie' ? 'number' : 'text'}
                            value={autocompleteValue[`${participant}-${line}`] || ''}
                            onChange={(e) =>
                              handlePickChange(participant, line, e.target.value)
                            }
                            onKeyDown={(e) =>
                              handlePickInputKeyDown(e, participant, line, lineIndex)
                            }
                            placeholder={line === 'Tie' ? 'Score' : 'Team'}
                            onFocus={() => {
                              if (line !== 'Tie') {
                                toggleAutocomplete(participant, line);
                              }
                            }}
                            onBlur={() => {
                              const key = `${participant}-${line}`;
                              setAutocompleteOpen((prev) => ({ ...prev, [key]: false }));
                              
                              // Clear value if it's not a valid team selection
                              if (line !== 'Tie') {
                                const currentValue = autocompleteValue[key] || '';
                                if (currentValue && !FOOTBALL_TEAMS.includes(currentValue)) {
                                  handlePickChange(participant, line, '');
                                }
                              }
                            }}
                            disabled={isLocked}
                            $status={cellStatus}
                          />
                          {line !== 'Tie' && !isLocked && autocompleteOpen[`${participant}-${line}`] && (
                            <AutocompleteList>
                              {getTeamSuggestions(
                                autocompleteValue[`${participant}-${line}`] || '',
                                getParticipantPickedTeams(participant)
                              ).map((team) => (
                                <AutocompleteItem
                                  key={team}
                                  onMouseDown={(e) => {
                                    e.preventDefault(); // prevents input blur from firing first
                                    selectTeam(participant, line, team);
                                  }}
                                >
                                  {team}
                                </AutocompleteItem>
                              ))}
                            </AutocompleteList>
                          )}
                        </PickCellContent>
                      )}
                    </PickCell>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </PicksTable>
      </TableWrapper>
      </PicksContent>
      </PicksLayout>
      <CreateUserModal 
        isOpen={isCreateUserModalOpen}
        onClose={() => setIsCreateUserModalOpen(false)}
        onUserCreated={handleUserCreated}
      />
      <DeleteUserConfirmModal 
        isOpen={isDeleteConfirmModalOpen}
        onClose={() => setIsDeleteConfirmModalOpen(false)}
        users={participants.map(p => ({ id: p, username: p, userId: participantUserIds[p] }))}
        onUserDeleted={handleUserDeleted}
      />
      {toastMessage && <Toast>{toastMessage}</Toast>}
    </TableContainer>
  );
};

export default TableComponent;
